/**
 * im12 全局 IM 实时通知 hook —— AppLayout 顶层调用【一次】。
 *
 * 主回路裁决（覆盖规格「复用单 socket」建议）：采用【独立通知专用全局 socket】方案。
 *   - 本 hook 建一条常驻的、独立于 Messenger 页级 socket 的连接（io('/im',{auth:{token}})），
 *     只订阅 message:new 做【站内/系统通知】；Messenger 自己的 socket 与全套事件订阅完全不动（零回归）。
 *   - 站内任何页都能即时收到新消息提示（不再只有停在 /im 才响）；离开 /im / 最小化也覆盖。
 *
 * 去重三处闸（避免双弹双响）：
 *   1) notifyNewMessage 内：用户正看 Messenger（onImVisible）时啥都不做（Messenger socket 即时刷消息体）。
 *   2) visible 但不在看 → app 内提示音 + toast；hidden → 交 OS 系统通知（跳过 app 内声音，后台标签声音多被节流）。
 *   3) Layout 30s 轮询涨值检测降为 socket 断线兜底（socket 在线时 return，见 Layout）。
 *
 * 边界：浏览器进程全关收不到 = 网页天花板，不做 Web Push（手机 App 覆盖）。
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../store/auth';
import { usePermissions } from '../lib/permissions';
import { notificationSound } from '../utils/notificationSound';
import { toast } from '../lib/toast';
import { ensurePermissionOnce, notifyIfHidden } from '../utils/desktopNotify';

/** message:new 推送的消息体（后端 broadcastMessage 直发 ImMessageEntity + enrich 的 senderRealName）。 */
interface ImNewMessage {
  id: number;
  conversationId: number;
  fromUserId: number | null;
  content: string;
  contentType?: string;
  senderRealName?: string;
}

/** 把消息按类型转成预览短文（图片/文件不显 URL，截断 24 字）。 */
function previewOf(m: ImNewMessage): string {
  const t = m.contentType;
  if (t === 'image') return '[图片]';
  if (t === 'file') return '[文件]';
  const s = String(m.content ?? '').slice(0, 24);
  return s || '[新消息]';
}

interface UseImRealtimeResult {
  /** 通知专用 socket 是否在线 —— 供 Layout 轮询涨值检测做断线兜底守卫（在线时轮询不弹，避免双触发）。 */
  connected: boolean;
}

/**
 * @param combinedUnread 合并未读数（IM 私信 + 系统通知），用于全局 document.title 前缀 (N)。
 */
export function useImRealtime(combinedUnread: number): UseImRealtimeResult {
  const token = useAuthStore((s) => s.accessToken);
  const me = useAuthStore((s) => s.user);
  const perms = usePermissions();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [connected, setConnected] = useState(false);

  // me/perms/nav 用 ref 持有：socket 回调闭包内取最新值，避免把它们塞进 effect deps 导致频繁重连。
  const meIdRef = useRef<number | null>(me?.id ?? null);
  meIdRef.current = me?.id ?? null;
  const isMutedRef = useRef(false); // 通知 socket 收不到会话 muted 标记，提示音去重交 play 的 own/debounce 即可
  const navRef = useRef(nav);
  navRef.current = nav;

  // enabled = 有 token 且有 im:view 权限（6 角色都有，但未加载时 fail-safe false）
  const enabled = !!token && perms.has('im:view');

  // ── 常驻通知 socket（独立于 Messenger 页级 socket）──
  // deps 仅 [token, enabled]：登录/续期(token 变)或权限就绪重连；登出(token=null→enabled=false)/卸载 close。
  useEffect(() => {
    if (!enabled) { setConnected(false); return; }
    const socket: Socket = io('/im', {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true, reconnectionDelay: 1000, reconnectionDelayMax: 30000,
    });
    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    socket.on('connect_error', () => setConnected(false));
    socket.on('message:new', (m: ImNewMessage) => {
      // a) 与 Messenger 同口径：刷新会话列表 / 合并未读数 / 铃铛通知数（即时、不等 30s 轮询）
      qc.invalidateQueries({ queryKey: ['im-conversations'] });
      qc.invalidateQueries({ queryKey: ['im-unread-total'] });
      qc.invalidateQueries({ queryKey: ['notifications-unread'] });
      // b) 统一通知去重逻辑
      notifyNewMessage(m);
    });
    return () => { socket.close(); setConnected(false); };
  }, [token, enabled, qc]);

  // 统一新消息通知（去重核心）。
  function notifyNewMessage(m: ImNewMessage) {
    try {
      // 自己发的消息不提示（兜底；play 内也有 own-skip）
      const myId = meIdRef.current;
      if (myId != null && m.fromUserId != null && Number(m.fromUserId) === Number(myId)) return;

      const onImVisible =
        window.location.pathname.startsWith('/im') && document.visibilityState === 'visible';
      // 用户正看 Messenger：啥都不做（Messenger 自己的 socket 即时刷消息体）
      if (onImVisible) return;

      const who = m.senderRealName || '新消息';
      const preview = previewOf(m);
      const convId = Number(m.conversationId);
      const goConv = () => navRef.current(`/im?conv=${convId}`);

      if (document.visibilityState === 'hidden') {
        // 页面不可见：交 OS 系统通知（自带提示音），跳过 app 内声音（后台标签声音多被节流/block）
        notifyIfHidden(who, preview, goConv, `im-${convId}`);
      } else {
        // 页面可见但不在 /im：app 内提示音 + toast「发件人：内容摘要」点击跳会话，显示 5s
        notificationSound.play('message', {
          fromUserId: m.fromUserId,
          currentUserId: myId,
          isMuted: isMutedRef.current,
        });
        toast.imMessage(`${who}：${preview}`, goConv, 5000);
      }
    } catch {
      /* 通知失败不影响主流程 */
    }
  }

  // 首次进 /im 时引导系统通知授权（浏览器手势要求下，进页时机最稳；内部已去重不复问）。
  useEffect(() => {
    if (!enabled) return;
    if (window.location.pathname.startsWith('/im')) ensurePermissionOnce();
  });

  // 全局 document.title 未读前缀 (N)：任何页生效（替代原 Messenger 页级写法、避免双写）。
  useEffect(() => {
    const orig = document.title.replace(/^\(\d+\+?\)\s*/, '');
    document.title = combinedUnread > 0
      ? `(${combinedUnread > 99 ? '99+' : combinedUnread}) ${orig}`
      : orig;
  }, [combinedUnread]);

  return { connected };
}
